<template>
  <div class="card quote-history">
    <div class="card-header bg-white d-flex align-items-center gap-2">
      <AppIcon name="bi-clock-history" class="bi bi-clock-history" aria-hidden="true" />
      <span>{{ $t('history') }}</span>
    </div>

    <div class="card-body">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">{{ $t('loading') }}</span>
        </div>
      </div>

      <div v-else-if="history.length === 0" class="text-center text-muted py-4">
        {{ $t('no_history_found') }}
      </div>

      <template v-else>
        <div class="quote-history-list">
          <article
            v-for="item in history"
            :key="item.id"
            class="quote-history-entry"
          >
            <div class="quote-history-marker" :class="getActionClass(item.action)">
              <AppIcon :name="getActionIcon(item.action)" :class="getActionIcon(item.action)" aria-hidden="true" />
            </div>

            <div class="quote-history-content">
              <div class="quote-history-heading">
                <div>
                  <h6 class="mb-1">{{ getActionLabel(item.action) }}</h6>
                  <div class="quote-history-user">
                    <span class="quote-history-avatar" aria-hidden="true">
                      {{ getInitials(item.user && item.user.name) }}
                    </span>
                    <strong>{{ item.user && item.user.name ? item.user.name : $t('unknown') }}</strong>
                  </div>
                </div>
                <time class="quote-history-date" :datetime="item.created_at">
                  {{ formatDateTime(item.created_at) }}
                </time>
              </div>

              <div v-if="isEditAction(item.action)" class="quote-history-changes">
                <div
                  v-for="(change, index) in getChanges(item)"
                  :key="`${item.id}-${change.key}-${index}`"
                  class="quote-history-change"
                >
                  <span class="quote-history-field">{{ change.label }}</span>
                  <div class="quote-history-values">
                    <template v-if="change.showOldValue">
                      <span class="quote-history-old">{{ change.oldValue }}</span>
                      <AppIcon name="bi-arrow-right" class="bi bi-arrow-right quote-history-arrow" aria-hidden="true" />
                    </template>
                    <span class="quote-history-new">{{ change.newValue }}</span>
                  </div>
                </div>

                <ul v-if="item.details && item.details.changes" class="list-unstyled small mb-0">
                  <li v-for="(message, index) in item.details.changes" :key="index">
                    {{ formatHistoryMessage(message) }}
                  </li>
                </ul>
                <p v-else-if="item.details && item.details.message" class="small mb-0">
                  {{ item.details.message }}
                </p>
                <p v-else-if="getChanges(item).length === 0" class="text-muted small mb-0">
                  {{ $t('no_visible_changes') }}
                </p>
              </div>
              <div
                v-else-if="item.details && (item.details.changes || item.details.message)"
                class="quote-history-changes"
              >
                <ul v-if="item.details.changes" class="list-unstyled small mb-0">
                  <li v-for="(message, index) in item.details.changes" :key="index">
                    {{ formatHistoryMessage(message) }}
                  </li>
                </ul>
                <p v-else class="small mb-0">{{ item.details.message }}</p>
              </div>
            </div>
          </article>
        </div>

        <div
          v-if="pagination.total_pages > 1"
          class="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4"
        >
          <div class="text-muted small">
            {{ $t('showing') }} {{ firstVisibleRecord }} - {{ lastVisibleRecord }}
            {{ $t('of') }} {{ pagination.count }} {{ $t('records') }}
          </div>
          <nav :aria-label="$t('history')">
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                <button
                  type="button"
                  class="page-link"
                  :disabled="pagination.current_page === 1"
                  @click="$emit('pageChange', pagination.current_page - 1)"
                >
                  {{ $t('previous') }}
                </button>
              </li>
              <li
                v-for="page in getPageNumbers()"
                :key="page"
                class="page-item"
                :class="{ active: page === pagination.current_page }"
              >
                <button type="button" class="page-link" @click="$emit('pageChange', page)">
                  {{ page }}
                </button>
              </li>
              <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
                <button
                  type="button"
                  class="page-link"
                  :disabled="pagination.current_page === pagination.total_pages"
                  @click="$emit('pageChange', pagination.current_page + 1)"
                >
                  {{ $t('next') }}
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import helper from '../helper'

const MONEY_FIELDS = ['price', 'total', 'sub_total', 'total_discount', 'amount_paid', 'amount', 'total_amount']
const DATE_FIELDS = ['date', 'due_date', 'sale_date', 'start_date', 'end_date']
const PRODUCT_FIELDS = [
  { key: 'product_label', label: 'product_label', aliases: ['product_label', 'name'] },
  { key: 'quantity', label: 'quantity', aliases: ['quantity'] },
  { key: 'price', label: 'price', aliases: ['price', 'unit_price'] },
  { key: 'discount', label: 'discount', aliases: ['discount'] },
  { key: 'vat', label: 'vat', aliases: ['vat'] },
  { key: 'sub_total', label: 'sub_total', aliases: ['sub_total', 'subtotal'] },
  { key: 'total', label: 'total', aliases: ['total'] }
]

export default {
  name: 'DocumentHistory',
  props: {
    history: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Object,
      default: () => ({
        count: 0,
        per_page: 10,
        current_page: 1,
        total_pages: 1
      })
    }
  },
  computed: {
    firstVisibleRecord() {
      return ((this.pagination.current_page - 1) * this.pagination.per_page) + 1
    },
    lastVisibleRecord() {
      return Math.min(this.pagination.current_page * this.pagination.per_page, this.pagination.count)
    }
  },
  methods: {
    getActionLabel(action) {
      const normalizedAction = this.normalizeAction(action)
      const labels = {
        add: 'created',
        add_draft: 'draft_created',
        edit: 'updated',
        update: 'updated',
        updated: 'updated',
        created: 'created',
        delete: 'deleted',
        deleted: 'deleted',
        trash: 'deleted',
        restore: 'restored',
        restored: 'restored',
        comment_added: 'comment_added',
        comment_updated: 'comment_updated',
        comment_deleted: 'comment_deleted',
        paused: 'paused',
        resumed: 'resumed',
        frozen: 'frozen',
        renewed: 'renewed',
        canceled: 'canceled'
      }
      return this.$t(labels[normalizedAction] || normalizedAction)
    },
    getActionClass(action) {
      const normalizedAction = this.normalizeAction(action)
      if (['edit', 'update', 'updated', 'restore', 'restored', 'resumed'].includes(normalizedAction)) {
        return 'quote-history-marker--updated'
      }
      if (['delete', 'deleted', 'trash', 'canceled'].includes(normalizedAction)) {
        return 'quote-history-marker--deleted'
      }
      return 'quote-history-marker--created'
    },
    getActionIcon(action) {
      const normalizedAction = this.normalizeAction(action)
      if (['edit', 'update', 'updated'].includes(normalizedAction)) return 'bi bi-pencil'
      if (['delete', 'deleted', 'trash', 'canceled'].includes(normalizedAction)) return 'bi bi-trash'
      if (['restore', 'restored', 'resumed'].includes(normalizedAction)) return 'bi bi-arrow-counterclockwise'
      return 'bi bi-plus-lg'
    },
    normalizeAction(action) {
      return String(action || '').toLowerCase()
    },
    isEditAction(action) {
      return ['edit', 'update', 'updated'].includes(this.normalizeAction(action))
    },
    getInitials(name) {
      if (!name) return '?'
      return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')
    },
    getChanges(item) {
      const data = item && item.data && typeof item.data === 'object' ? item.data : {}
      const oldData = data.old && typeof data.old === 'object' ? data.old : {}
      const newData = data.new && typeof data.new === 'object' ? data.new : (this.isEditAction(item.action) ? data : {})
      const changes = []
      const fields = [
        { key: 'title', label: 'title' },
        { key: 'contract_number', label: 'contract_number' },
        { key: 'reference', label: 'reference' },
        { key: 'date', label: 'date' },
        { key: 'due_date', label: 'due_date' },
        { key: 'sale_date', label: 'date' },
        { key: 'start_date', label: 'start_date' },
        { key: 'end_date', label: 'end_date' },
        { key: 'client_id', label: 'client' },
        { key: 'supplier_id', label: 'supplier' },
        { key: 'plan_id', label: 'plan' },
        { key: 'currency', label: 'currency' },
        { key: 'status', label: 'status' },
        { key: 'payment_status', label: 'payment_status' },
        { key: 'payment_term', label: 'payment_term' },
        { key: 'payment_method', label: 'payment_method' },
        { key: 'pm_reference', label: 'transaction_id' },
        { key: 'amount_paid', label: 'amount_paid' },
        { key: 'amount', label: 'amount' },
        { key: 'type', label: 'type' },
        { key: 'auto_renew', label: 'auto_renew' },
        { key: 'billing_frequency', label: 'billing_frequency' },
        { key: 'discount', label: 'discount' },
        { key: 'total_amount', label: 'total_amount' },
        { key: 'cashier_id', label: 'cashier' },
        { key: 'cash_register_id', label: 'cash_register' },
        { key: 'client_po_number', label: 'client_po_number' },
        { key: 'client_in_number', label: 'client_in_number' },
        { key: 'client_dn_number', label: 'client_dn_number' },
        { key: 'notes', label: 'notes' },
        { key: 'total', label: 'total' },
        { key: 'sub_total', label: 'sub_total' },
        { key: 'total_discount', label: 'total_discount' }
      ]

      fields.forEach(field => {
        if (!Object.prototype.hasOwnProperty.call(newData, field.key)) return
        if (this.valuesMatch(field.key, oldData[field.key], newData[field.key])) return

        changes.push({
          key: field.key,
          label: this.$t(field.label),
          showOldValue: this.hasValue(field.key, oldData[field.key]),
          oldValue: this.formatFieldValue(field.key, oldData[field.key], oldData),
          newValue: this.formatFieldValue(field.key, newData[field.key], newData)
        })
      })

      if (Object.prototype.hasOwnProperty.call(newData, 'products')) {
        changes.push(...this.getProductChanges(oldData.products || [], newData.products || []))
      }

      return changes
    },
    getProductChanges(oldProducts, newProducts) {
      const changes = []
      const matchedOldIndexes = new Set()
      const matches = []

      newProducts.forEach((newProduct, newIndex) => {
        const oldIndex = this.findProductIndex(newProduct, oldProducts, matchedOldIndexes)
        if (oldIndex === -1) {
          changes.push({
            key: `product-added-${newIndex}`,
            label: this.productName(newProduct, newIndex),
            showOldValue: false,
            oldValue: '',
            newValue: this.$t('added')
          })
          return
        }

        matchedOldIndexes.add(oldIndex)
        matches.push({ oldIndex, newIndex })
        const oldProduct = oldProducts[oldIndex]

        PRODUCT_FIELDS.forEach(field => {
          const oldField = this.getProductField(oldProduct, field.aliases)
          const newField = this.getProductField(newProduct, field.aliases)
          if (!newField.exists) return
          if (field.key === 'product_label' && !oldField.exists) return
          if (this.valuesMatch(field.key, oldField.value, newField.value)) return

          changes.push({
            key: `product-${oldIndex}-${field.key}`,
            label: `${this.productName(newProduct, newIndex)} · ${this.$t(field.label)}`,
            showOldValue: this.hasValue(field.key, oldField.value),
            oldValue: this.formatFieldValue(field.key, oldField.value, oldProduct),
            newValue: this.formatFieldValue(field.key, newField.value, newProduct)
          })
        })
      })

      oldProducts.forEach((oldProduct, oldIndex) => {
        if (matchedOldIndexes.has(oldIndex)) return
        changes.push({
          key: `product-removed-${oldIndex}`,
          label: this.productName(oldProduct, oldIndex),
          showOldValue: false,
          oldValue: '',
          newValue: this.$t('removed')
        })
      })

      const reordered = matches.length === oldProducts.length
        && matches.length === newProducts.length
        && matches.some(match => match.oldIndex !== match.newIndex)

      if (reordered) {
        changes.unshift({
          key: 'product-order',
          label: this.$t('products'),
          showOldValue: true,
          oldValue: oldProducts.map(this.productName).join(', '),
          newValue: newProducts.map(this.productName).join(', ')
        })
      }

      return changes
    },
    findProductIndex(product, products, matchedIndexes) {
      const available = (predicate) => products.findIndex((candidate, index) => (
        !matchedIndexes.has(index) && predicate(candidate)
      ))

      if (product.id) {
        const idMatch = available(candidate => candidate.id && String(candidate.id) === String(product.id))
        if (idMatch !== -1) return idMatch
      }

      if (product.inventory_id) {
        const inventoryMatch = available(candidate => (
          candidate.inventory_id && String(candidate.inventory_id) === String(product.inventory_id)
        ))
        if (inventoryMatch !== -1) return inventoryMatch
      }

      if (product.product_id) {
        const productMatch = available(candidate => (
          candidate.product_id && String(candidate.product_id) === String(product.product_id)
        ))
        if (productMatch !== -1) return productMatch
      }

      return available(candidate => this.productName(candidate, 0) === this.productName(product, 0))
    },
    getProductField(product, aliases) {
      const key = aliases.find(alias => Object.prototype.hasOwnProperty.call(product || {}, alias))
      return key ? { exists: true, value: product[key] } : { exists: false, value: undefined }
    },
    productName(product, index) {
      const label = this.plainText(product && (product.product_label || product.name || product.product?.label))
      return label || `${this.$t('products')} #${Number(index) + 1}`
    },
    plainText(value) {
      return String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/\s+/g, ' ')
        .trim()
    },
    valuesMatch(field, oldValue, newValue) {
      if ((oldValue === null || oldValue === undefined || oldValue === '')
        && (newValue === null || newValue === undefined || newValue === '')) return true
      if (DATE_FIELDS.includes(field)) {
        return helper.formatDate(oldValue) === helper.formatDate(newValue)
      }
      if (field === 'product_label') return this.plainText(oldValue) === this.plainText(newValue)
      if (MONEY_FIELDS.includes(field) || ['quantity', 'discount', 'vat'].includes(field)) {
        return Number(oldValue || 0) === Number(newValue || 0)
      }
      return String(oldValue) === String(newValue)
    },
    hasValue(field, value) {
      if (value === null || value === undefined || value === '') return false
      if (field === 'product_label') return this.plainText(value) !== ''
      return true
    },
    formatFieldValue(field, value, record) {
      if (field === 'client_id' && record && record.client_name) return record.client_name
      if (field === 'supplier_id' && record && record.supplier_name) return record.supplier_name
      if (field === 'plan_id' && record && record.plan_name) return record.plan_name
      if (field === 'cashier_id' && record && record.cashier_name) return record.cashier_name
      if (field === 'cash_register_id' && record && record.cash_register_name) return record.cash_register_name
      if (value === null || value === undefined || value === '') return '—'
      if (DATE_FIELDS.includes(field)) return helper.formatDate(value)
      if (['status', 'payment_status', 'payment_method', 'type'].includes(field)) return this.$t(value)
      if (field === 'product_label') return this.plainText(value) || '—'
      if (MONEY_FIELDS.includes(field)) return helper.formatMoney(value)
      if (field === 'discount' || field === 'vat') return `${value}%`
      if (field === 'auto_renew') return this.$t(value ? 'yes' : 'no')
      return String(value)
    },
    formatHistoryMessage(message) {
      return String(message || '').replace(/ changed from (?:—|-) to /, ' updated to ')
    },
    getPageNumbers() {
      const current = Number(this.pagination.current_page)
      const total = Number(this.pagination.total_pages)
      let start = Math.max(1, current - 2)
      let end = Math.min(total, current + 2)

      if (end - start < 4) {
        if (start === 1) end = Math.min(total, start + 4)
        else start = Math.max(1, end - 4)
      }

      const pages = []
      for (let page = start; page <= end; page += 1) pages.push(page)
      return pages
    },
    formatDateTime(date) {
      return helper.formatDateTime(date)
    }
  }
}
</script>

<style scoped>
.quote-history-list {
  position: relative;
}

.quote-history-list::before {
  position: absolute;
  top: 20px;
  bottom: 20px;
  left: 18px;
  width: 2px;
  content: '';
  background: #e9ecef;
}

.quote-history-entry {
  position: relative;
  display: flex;
  gap: 1rem;
  padding-bottom: 1.25rem;
}

.quote-history-entry:last-child {
  padding-bottom: 0;
}

.quote-history-marker {
  z-index: 1;
  display: inline-flex;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 4px solid #fff;
  border-radius: 50%;
}

.quote-history-marker--created {
  color: #146c43;
  background: #d1e7dd;
}

.quote-history-marker--updated {
  color: #084298;
  background: #cfe2ff;
}

.quote-history-marker--deleted {
  color: #842029;
  background: #f8d7da;
}

.quote-history-content {
  min-width: 0;
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid #e9ecef;
  border-radius: 0.6rem;
  background: #fff;
}

.quote-history-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.quote-history-user {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: #495057;
  font-size: 0.82rem;
}

.quote-history-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  color: #fff;
  border-radius: 50%;
  background: #6c757d;
  font-size: 0.65rem;
  font-weight: 700;
}

.quote-history-date {
  color: #6c757d;
  font-size: 0.78rem;
  white-space: nowrap;
}

.quote-history-changes {
  display: grid;
  gap: 0.55rem;
  margin-top: 0.85rem;
  padding-top: 0.85rem;
  border-top: 1px solid #f0f1f2;
}

.quote-history-change {
  display: grid;
  grid-template-columns: minmax(120px, 0.35fr) minmax(0, 1fr);
  align-items: center;
  gap: 1rem;
}

.quote-history-field {
  color: #495057;
  font-size: 0.82rem;
  font-weight: 600;
}

.quote-history-values {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.65rem;
  font-size: 0.82rem;
}

.quote-history-old,
.quote-history-new {
  overflow-wrap: anywhere;
}

.quote-history-old {
  color: #842029;
  text-decoration: line-through;
}

.quote-history-new {
  color: #146c43;
  font-weight: 600;
}

.quote-history-arrow {
  flex: 0 0 auto;
  color: #adb5bd;
}

@media (max-width: 767.98px) {
  .quote-history-heading,
  .quote-history-change {
    display: block;
  }

  .quote-history-date {
    display: block;
    margin-top: 0.5rem;
    white-space: normal;
  }

  .quote-history-values {
    margin-top: 0.3rem;
  }
}
</style>
